import { go } from 'fxjs/es';
import { createCanvasElement, loadImageFromUrl } from '../../../Canvas/S/util.js';
import { UtilS } from '../../../Util/S/Function/module/UtilS.js';
/**
 * Canvas 관련 유틸리티 기능을 제공하는 클래스입니다.
 */
export class CanvasUtils {
  /**
   * 캔버스 컨텍스트를 가져옵니다.
   * @param {HTMLCanvasElement} canvas - 캔버스 엘리먼트
   * @param {boolean} willReadFrequently - 빈번한 읽기 작업 여부
   * @returns {CanvasRenderingContext2D} 캔버스 컨텍스트
   * @throws {Error} 컨텍스트를 가져올 수 없는 경우
   * @private
   */
  static #getContext(canvas, willReadFrequently = false) {
    const ctx = canvas.getContext('2d', { willReadFrequently });
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    return ctx;
  }

  /**
   * URL로부터 이미지를 로드하여 캔버스를 생성합니다.
   * @param {Object} params - 파라미터 객체
   * @param {string} params.url - 이미지 URL
   * @param {boolean} [params.isPixelated=true] - 픽셀화 스타일 적용 여부
   * @returns {Promise<HTMLCanvasElement>} 생성된 캔버스
   * @throws {Error} 이미지 로드 실패 시
   */
  static async makeCanvasByUrl({ url, isPixelated = true }) {
    return go(await loadImageFromUrl(UtilS.ensureUrlProtocol(url)), (img) => {
      const canvas = createCanvasElement({
        width: img.width,
        height: img.height,
      });
      const ctx = this.#getContext(canvas);

      // 픽셀화 스타일 설정
      ctx.imageSmoothingEnabled = !isPixelated;

      ctx.drawImage(img, 0, 0);
      return canvas;
    });
  }

  /**
   * 캔버스를 리사이징합니다.
   * @param {Object} params - 파라미터 객체
   * @param {HTMLCanvasElement} params.canvas - 리사이징할 캔버스
   * @param {number} [params.scale] - 스케일 값
   * @param {boolean} [params.isPixelated=true] - 픽셀화 스타일 적용 여부
   * @throws {Error} 유효하지 않은 입력값이나 이미지 처리 실패 시
   * @returns {HTMLCanvasElement} 리사이징된 캔버스
   */
  static resizeCanvas({ canvas, scale, isPixelated = true }) {
    if (!canvas || !canvas.getContext || typeof canvas.getContext !== 'function') {
      throw new Error('Invalid canvas: canvas must be a valid canvas element');
    }

    if (!Number.isFinite(scale) || scale <= 0) {
      throw new Error('Invalid scale: scale must be a positive number');
    }

    const { width: originalWidth, height: originalHeight } = canvas;
    const maxDimension = Math.max(originalWidth, originalHeight);
    const newScale = (scale * maxDimension) / maxDimension; // 비율 유지

    const newWidth = Math.round(originalWidth * newScale);
    const newHeight = Math.round(originalHeight * newScale);

    const resizedCanvas = createCanvasElement({ width: newWidth, height: newHeight });
    const ctx = this.#getContext(resizedCanvas);

    // 픽셀화 스타일 설정
    ctx.imageSmoothingEnabled = !isPixelated;
    ctx.drawImage(canvas, 0, 0, newWidth, newHeight);

    return resizedCanvas;
  }

  /**
   * Canvas의 투명한 영역을 제외한 실제 이미지 영역의 경계를 계산합니다.
   * @typedef {Object} ImageBounds
   * @property {number} top - 상단 여백
   * @property {number} bottom - 하단 여백
   * @property {number} left - 좌측 여백
   * @property {number} right - 우측 여백
   * @property {number} width - 실제 이미지 너비
   * @property {number} height - 실제 이미지 높이
   */

  /**
   * 주어진 위치의 픽셀 알파값이 임계값보다 큰지 확인합니다.
   * @param {ImageData} imageData - 캔버스의 이미지 데이터
   * @param {number} x - x 좌표
   * @param {number} y - y 좌표
   * @param {number} width - 이미지 너비
   * @param {number} threshold - 알파값 임계값
   * @returns {boolean} 알파값이 임계값보다 크면 true
   * @private
   */
  static #hasVisiblePixel(imageData, x, y, width, threshold = 1) {
    return imageData.data[(y * width + x) * 4 + 3] > threshold;
  }

  /**
   * 주어진 행이 모두 투명한지 확인합니다.
   * @param {ImageData} imageData - 캔버스의 이미지 데이터
   * @param {number} y - 검사할 행 번호
   * @param {number} threshold - 알파값 임계값
   * @returns {boolean} 해당 행이 모두 투명하면 true
   * @private
   */
  static #isRowEmpty(imageData, y, threshold = 1) {
    const { width } = imageData;
    for (let x = 0; x < width; x++) {
      if (this.#hasVisiblePixel(imageData, x, y, width, threshold)) {
        return false;
      }
    }
    return true;
  }

  /**
   * 주어진 열이 모두 투명한지 확인합니다.
   * @param {ImageData} imageData - 캔버스의 이미지 데이터
   * @param {number} x - 검사할 열 번호
   * @param {number} topBound - 검사 시작 y좌표
   * @param {number} bottomBound - 검사 종료 y좌표
   * @param {number} threshold - 알파값 임계값
   * @returns {boolean} 해당 열이 모두 투명하면 true
   * @private
   */
  static #isColumnEmpty(imageData, x, topBound, bottomBound, threshold = 1) {
    const { width } = imageData;
    for (let y = topBound; y < bottomBound; y++) {
      if (this.#hasVisiblePixel(imageData, x, y, width, threshold)) {
        return false;
      }
    }
    return true;
  }

  /**
   * 캔버스의 실제 이미지 영역 경계를 계산합니다.
   * @param {HTMLCanvasElement} canvas - 분석할 캔버스
   * @param {number} [threshold=1] - 알파값 임계값 (0-255)
   * @returns {ImageBounds} 이미지 경계 정보
   */
  static calculateImageBounds(canvas, threshold = 1) {
    const ctx = this.#getContext(canvas, true); // 빈번한 getImageData 호출이 있으므로 willReadFrequently를 true로 설정

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const { width, height } = imageData;

    // 상단 경계 찾기
    let top = 0;
    while (top < height && this.#isRowEmpty(imageData, top, threshold)) {
      top++;
    }

    // 하단 경계 찾기
    let bottom = height - 1;
    while (bottom > top && this.#isRowEmpty(imageData, bottom, threshold)) {
      bottom--;
    }

    // 좌측 경계 찾기
    let left = 0;
    while (left < width && this.#isColumnEmpty(imageData, left, top, bottom + 1, threshold)) {
      left++;
    }

    // 우측 경계 찾기
    let right = width - 1;
    while (right > left && this.#isColumnEmpty(imageData, right, top, bottom + 1, threshold)) {
      right--;
    }

    return {
      top,
      bottom,
      left,
      right,
      width: right - left + 1,
      height: bottom - top + 1,
    };
  }

  /**
   * 캔버스의 투명한 여백을 제거하고 새로운 캔버스를 반환합니다.
   * @param {Object} params - 파라미터 객체
   * @param {HTMLCanvasElement} params.canvas - 여백을 제거할 원본 캔버스
   * @param {number} params.padding
   * @param {boolean} [params.isPixelated=true] - 픽셀화 스타일 적용 여부
   * @returns {{canvas: HTMLCanvasElement, trimmedLength: {top: number, bottom: number, left: number, right: number}}} 여백이 제거된 새로운 캔버스, 트리밍된 상하좌우 길이
   * @throws {Error} 캔버스 컨텍스트를 가져올 수 없는 경우
   */
  static trimPadding({ canvas, padding = 0, isPixelated = true }) {
    const sourceCtx = this.#getContext(canvas, false);

    return go(this.calculateImageBounds(canvas), ({ top, bottom, left, right, width, height }) => {
      // padding을 고려한 새로운 크기 계산
      const paddedWidth = width + padding * 2;
      const paddedHeight = height + padding * 2;

      // 새 캔버스 생성 및 크기 설정 (padding 포함)
      const trimmedCanvas = createCanvasElement({
        width: paddedWidth,
        height: paddedHeight,
      });

      const trimmedCtx = this.#getContext(trimmedCanvas);

      // 픽셀화 스타일 설정
      trimmedCtx.imageSmoothingEnabled = !isPixelated;

      // 원본 캔버스에서 실제 이미지 영역의 데이터를 추출
      const imageData = sourceCtx.getImageData(left, top, width, height);

      // padding을 고려하여 이미지를 중앙에 배치
      trimmedCtx.putImageData(imageData, padding, padding);

      return {
        canvas: trimmedCanvas,
        trimmedLength: {
          top: top - padding,
          bottom: canvas.height - (bottom + padding),
          left: left - padding,
          right: canvas.width - (right + padding),
        },
      };
    });
  }

  /**
   * 캔버스를 지정된 경계 영역에 맞게 크롭합니다.
   * @static
   * @param {Object} params - 파라미터 객체
   * @param {HTMLCanvasElement} params.canvas - 크롭할 캔버스
   * @param {Object} params.bounds - 크롭할 경계 영역
   * @param {number} params.bounds.x - x 좌표
   * @param {number} params.bounds.y - y 좌표
   * @param {number} params.bounds.width - 너비
   * @param {number} params.bounds.height - 높이
   * @param {boolean} [params.isPixelated=true] - 픽셀화 스타일 적용 여부
   * @returns {HTMLCanvasElement} 크롭된 캔버스
   */
  static cropCanvas({ canvas, bounds, isPixelated = true }) {
    const croppedCanvas = createCanvasElement({
      width: bounds.width,
      height: bounds.height,
    });

    const ctx = this.#getContext(croppedCanvas);
    ctx.clearRect(0, 0, bounds.width, bounds.height);

    // 픽셀화 스타일 설정
    ctx.imageSmoothingEnabled = !isPixelated;

    // 원본 캔버스에서 bounds 영역만큼 크롭하여 새 캔버스에 그리기
    ctx.drawImage(
      canvas,
      bounds.x,
      bounds.y,
      bounds.width,
      bounds.height, // 소스 영역 (크롭할 영역)
      0,
      0,
      bounds.width,
      bounds.height, // 대상 영역 (전체 캔버스)
    );

    return croppedCanvas;
  }

  /**
   * 이미지를 반복 패턴으로 그립니다.
   * @param {Object} params - 파라미터 객체
   * @param {CanvasRenderingContext2D} params.ctx - 캔버스 컨텍스트
   * @param {HTMLImageElement} params.image - 반복할 이미지
   * @param {number} params.x - 시작 x 좌표
   * @param {number} params.y - 시작 y 좌표
   * @param {number} params.width - 전체 그리기 영역 너비
   * @param {number} params.height - 전체 그리기 영역 높이
   * @param {boolean} [params.isPixelated=true] - 픽셀화 스타일 적용 여부
   * @param {number} [params.overlap=0] - 세그먼트 간 오버랩 픽셀 수
   */
  static drawRepeatingImage({ ctx, image, x, y, width, height, isPixelated = true, overlap = 0 }) {
    const originalSmoothing = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = !isPixelated;

    const segmentWidth = (image.width / image.height) * height;
    const repeatCount = Math.ceil(width / segmentWidth);

    let currentX = x;
    for (let i = 0; i < repeatCount; i++) {
      const isLastSegment = i === repeatCount - 1;
      const remainingWidth = x + width - currentX;
      const drawWidth = isLastSegment ? remainingWidth + overlap : segmentWidth;

      const sourceWidth = isLastSegment
        ? ((remainingWidth + overlap) / segmentWidth) * image.width
        : image.width;

      ctx.drawImage(
        image,
        0,
        0,
        sourceWidth,
        image.height,
        currentX,
        y,
        drawWidth + (isLastSegment ? 0 : overlap),
        height,
      );

      currentX += drawWidth;
    }

    ctx.imageSmoothingEnabled = originalSmoothing;
  }

  /**
   * 이미지를 pixelated 스타일로 그립니다.
   * @param {Object} params - 파라미터 객체
   * @param {CanvasRenderingContext2D} params.ctx - 캔버스 컨텍스트
   * @param {HTMLImageElement} params.image - 그릴 이미지
   * @param {number} params.x - 시작 x 좌표
   * @param {number} params.y - 시작 y 좌표
   * @param {number} params.width - 그리기 영역 너비
   * @param {number} params.height - 그리기 영역 높이
   * @param {number} [params.overlap=0] - 오버랩 픽셀 수
   */
  static drawPixelatedImage({ ctx, image, x, y, width, height, overlap = 0 }) {
    const originalSmoothing = ctx.imageSmoothingEnabled;
    ctx.imageSmoothingEnabled = false;

    ctx.drawImage(image, 0, 0, image.width, image.height, x, y, width + overlap, height);

    ctx.imageSmoothingEnabled = originalSmoothing;
  }
}
